"use client";

import { cn } from "@/lib";
import Link from "next/link";
import React, { ComponentProps, useRef } from "react";
import { FlexBetween } from "./ui";
import { BdAltArrowDown } from "solar-icon-react/bd";
import { TServiceNavigationItem } from "@/interfaces";

interface Props extends ComponentProps<"section"> {
  navData: TServiceNavigationItem[];
  activeKey: string;
  handleClick?: (itemKey: string) => void;
  forceScrollable?: boolean;
}

const QuickAccessServicesNavigation = ({
  navData,
  activeKey,
  handleClick,
  forceScrollable = false,
  className,
}: Props) => {
  const listRef = useRef<HTMLUListElement>(null);
  const shouldUseScrollableLayout = forceScrollable || navData.length > 4;

  const scrollList = (direction: "left" | "right") => {
    if (!listRef.current) return;

    const scrollAmount = Math.max(listRef.current.clientWidth * 0.75, 180);

    listRef.current.scrollBy({
      left: direction === "right" ? scrollAmount : -scrollAmount,
      behavior: "smooth",
    });
  };

  const getItemClassName = (item: TServiceNavigationItem) =>
    cn(
      "hover:text-secondary-main flex-center min-w-max shrink-0 snap-start cursor-pointer rounded-lg text-center text-black transition-colors",
      activeKey == item.key && "bg-primary-main text-white shadow-sm",
      item.disable && "pointer-events-none text-gray-400",
    );

  const renderItemContent = (item: TServiceNavigationItem) => (
    <span className="flex items-center justify-center gap-2 whitespace-nowrap">
      <span>{item.label}</span>
    </span>
  );

  return (
    <section className={cn("relative z-30", className)}>
      <nav
        className={cn(
          "shadow-main relative w-full overflow-hidden rounded-xl bg-white px-3.5 py-2.5 text-xs lg:px-6 lg:py-4 lg:text-sm xl:block xl:text-base",
        )}
      >
        {shouldUseScrollableLayout ? (
          <>
            <button
              type="button"
              aria-label="اسکرول به راست"
              onClick={() => scrollList("right")}
              className="text-primary-main absolute top-1/2 right-2 z-20 hidden size-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/95 shadow-sm transition hover:bg-slate-50 md:flex"
            >
              <BdAltArrowDown className="size-5! -rotate-90!" />
            </button>
            <button
              type="button"
              aria-label="اسکرول به چپ"
              onClick={() => scrollList("left")}
              className="text-primary-main absolute top-1/2 left-2 z-20 hidden size-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/95 shadow-sm transition hover:bg-slate-50 md:flex"
            >
              <BdAltArrowDown className="size-5! rotate-90!" />
            </button>
          </>
        ) : null}

        <FlexBetween gap={2}>
          <span className="inline w-10 overflow-hidden sm:hidden">
            <BdAltArrowDown className="text-primary-main! size-full! -rotate-90!" />
          </span>
          <ul
            ref={listRef}
            className={cn(
              "no-scrollbar mx-auto flex min-w-0 grow overflow-x-auto",
              shouldUseScrollableLayout
                ? "snap-x snap-mandatory gap-2.5 px-10 md:px-14 lg:gap-3 lg:px-16"
                : "justify-between gap-2",
            )}
          >
            {navData.map((item, index) =>
              handleClick ? (
                <li
                  id={`landing-quick-access-${item.key.toLowerCase()}`}
                  key={index}
                  className={cn(
                    getItemClassName(item),
                    "px-4 py-2",
                    !shouldUseScrollableLayout && "md:px-3",
                  )}
                  onClick={() => {
                    if (!item.disable) handleClick(item.key);
                  }}
                >
                  {renderItemContent(item)}
                </li>
              ) : (
                <li key={index} className={cn(getItemClassName(item))}>
                  <Link
                    id={`landing-quick-access-${item.key.toLowerCase()}`}
                    href={!item.disable ? item.href : "#"}
                    className={cn(
                      "w-full px-4 py-2",
                      !shouldUseScrollableLayout && "md:px-3",
                    )}
                    scroll={false}
                  >
                    {renderItemContent(item)}
                  </Link>
                </li>
              ),
            )}
          </ul>
          <span className="inline w-10 overflow-hidden sm:hidden">
            <BdAltArrowDown className="text-primary-main! size-full! rotate-90!" />
          </span>
        </FlexBetween>
      </nav>
    </section>
  );
};

export default QuickAccessServicesNavigation;
